import { Form, Formik, Field, ErrorMessage } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import * as Yup from "yup";
import { getColdLeadAsync, updateColdLeadAsync, checkContactNumberExistsAsync } from "../redux";
import { Card, CardBody, CardHeader } from "../../../../_metronic/_partials/controls";
import { regexValidation } from "../../../../_metronic/_helpers";
import { showSuccessSnackbar } from "../../snackBar/snackBar.action";

const ViewColdLead = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { coldLeadDetails } = useSelector(
    (state) => ({
      coldLeadDetails: state.coldLeads.selectedColdLead,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (id) {
      dispatch(getColdLeadAsync(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const redirectBack = () => {
    history.push("/old-leads");
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(100, "Name must not exceed 100 characters"),
    email: Yup.string()
      .trim()
      .nullable()
      .notRequired()
      .test("email-format", "Enter valid email", function (value) {
        if (!value || value.length === 0) {
          return true; // Email is optional, so empty is valid
        }
        return regexValidation.EMAIL_REGEX.test(value);
      }),
    contactNumber: Yup.string()
      .trim()
      .required("Contact Number is required")
      .matches(regexValidation.CONTACT_NUMBER_REGEX, "Contact Number is not valid"),
    pro_requirements: Yup.string()
      .trim()
      .required("Project Requirements is required")

  });

  const initialValues = {
    name: coldLeadDetails?.name || "",
    email: coldLeadDetails?.email || "",
    contactNumber: coldLeadDetails?.contactNumber || "",
    pro_requirements: coldLeadDetails?.pro_requirements || "",
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setIsSubmitting(true);
    try {
      // Trim contact number for comparison
      const trimmedContactNumber = values.contactNumber?.trim() || "";
      const originalContactNumber = coldLeadDetails?.contactNumber?.trim() || "";

      // Check for duplicate contact number ONLY if the number has changed
      if (trimmedContactNumber !== originalContactNumber) {
        const duplicateCheck = await dispatch(checkContactNumberExistsAsync(trimmedContactNumber, id));

        // If duplicate exists, show error and stop
        if (duplicateCheck && duplicateCheck.exists === true) {
          setFieldError("contactNumber", "This number already exists");
          setIsSubmitting(false);
          setSubmitting(false);
          return;
        }
      }

      // Prepare data with trimmed values
      const submitData = {
        name: (values.name || "").trim(),
        email: (values.email || "").trim() || undefined,
        contactNumber: trimmedContactNumber,
        pro_requirements: (values.pro_requirements || "").trim(),
      };

      // Remove undefined values
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === undefined || submitData[key] === "") {
          delete submitData[key];
        }
      });

      const result = await dispatch(updateColdLeadAsync(id, submitData));

      if (result && result.success) {
        setTimeout(() => {
          redirectBack();
        }, 1000);
      } else {
        // Handle errors
        if (result && result.isDuplicate) {
          setFieldError("contactNumber", "This number already exists");
        } else if (result && result.error) {
          // Error snackbar is already shown by updateColdLeadAsync
        }
      }
    } catch (error) {
      console.error("Exception during update:", error);
      dispatch(showSuccessSnackbar("error", "Error while updating cold lead", 3000));
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const getInputClasses = (fieldname, touched, errors) => {
    if (touched && errors) {
      return "form-control re_inputRouded is-invalid";
    }
    if (touched && !errors) {
      return "form-control re_inputRouded is-valid";
    }
    return "form-control re_inputRouded";
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, touched, errors, isSubmitting: formikSubmitting }) => (
        <Row className="justify-content-center">
          <Col lg={11}>
            <Card>
              <CardHeader title="Edit Old Lead" />
              <CardBody>
                <Form>
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <Field
                      className={getInputClasses("name", touched.name, errors.name)}
                      placeholder="Name"
                      type="text"
                      name="name"
                    />
                    <ErrorMessage name="name" component="div" className="invalid-feedback" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <Field
                      className={getInputClasses("email", touched.email, errors.email)}
                      placeholder="Email"
                      type="email"
                      name="email"
                    />
                    <ErrorMessage name="email" component="div" className="invalid-feedback" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Contact No.</label>
                    <Field
                      className={getInputClasses("contactNumber", touched.contactNumber, errors.contactNumber)}
                      placeholder="Contact Number"
                      type="text"
                      name="contactNumber"
                    />
                    <ErrorMessage name="contactNumber" component="div" className="invalid-feedback" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Project Requirements</label>
                    <Field
                      as="textarea"
                      className={getInputClasses("pro_requirements", touched.pro_requirements, errors.pro_requirements)}
                      rows={5}
                      placeholder="Project Requirement"
                      name="pro_requirements"
                    />
                    <ErrorMessage name="pro_requirements" component="div" className="invalid-feedback" />
                  </div>
                  <div className="text-center">
                    <Button
                      variant="blue"
                      className="px-5 defpddng me-3 mr-3"
                      size="lg"
                      type="submit"
                      disabled={isSubmitting || formikSubmitting}
                    >
                      {isSubmitting || formikSubmitting ? "Saving..." : "Save"}
                    </Button>

                    <Button
                      variant="secondary"
                      className="px-5 defpddng"
                      size="lg"
                      onClick={redirectBack}
                      disabled={isSubmitting || formikSubmitting}
                    >
                      Close
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </Formik>
  );
};

export default ViewColdLead;


